// First-party ad attribution: the click ID and UTM tags of the last paid
// landing on www.seam.co, kept for console.seam.co to send to the Seam API
// with the session that creates an account. Never sent to any third party.

export const AD_ATTRIBUTION_COOKIE = "seam_ad_attribution"
const MAX_AGE = 90 * 24 * 60 * 60
const MAX_LENGTH = 512
// Leave room for the cookie name and attributes under the 4096-byte limit.
const MAX_COOKIE_VALUE = 3500

const CLICK_IDS = ["gclid", "gbraid", "wbraid"] as const
const URL_FIELDS = [
  ...CLICK_IDS,
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const
const FIELDS = [...URL_FIELDS, "landing_path", "captured_at"] as const

/** The `ad_attribution` body of `/internal/sessions/create`. Empty keys are absent. */
export type AdAttribution = Partial<Record<(typeof FIELDS)[number], string>>

const clean = (value: unknown) =>
  typeof value === "string" && value.trim() !== ""
    ? value.trim().slice(0, MAX_LENGTH)
    : undefined

const sanitize = (input: Record<string, unknown>): AdAttribution | null => {
  const result: AdAttribution = {}
  for (const field of FIELDS) {
    const value = clean(input[field])
    if (value) result[field] = value
  }
  return URL_FIELDS.some((field) => result[field]) ? result : null
}

export const hasClickId = (attribution: AdAttribution | null | undefined) =>
  CLICK_IDS.some((field) => Boolean(attribution?.[field]))

/** Tracked parameters of a landing URL, or null when it carries none. */
export function attributionFromUrl(
  href: string,
  now = new Date(),
): AdAttribution | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  const params: Record<string, unknown> = {}
  for (const field of URL_FIELDS) params[field] = url.searchParams.get(field)
  if (!URL_FIELDS.some((field) => clean(params[field]))) return null
  return sanitize({
    ...params,
    landing_path: url.pathname,
    captured_at: now.toISOString(),
  })
}

/**
 * Last paid click: a landing with a click ID always replaces the record; a
 * landing with only UTM tags replaces it only when the record has no click ID.
 */
export function nextAttribution(
  current: AdAttribution | null,
  landing: AdAttribution | null,
): AdAttribution | null {
  if (!landing) return current
  if (hasClickId(landing) || !hasClickId(current)) return landing
  return current
}

const cookieAttributes = () => {
  const { hostname, protocol } = window.location
  const shared = hostname === "seam.co" || hostname.endsWith(".seam.co")
  // Host-only elsewhere: localhost cookies are shared across dev ports.
  return `${shared ? "; Domain=seam.co" : ""}; Path=/; SameSite=Lax${
    protocol === "https:" ? "; Secure" : ""
  }`
}

export function readAdAttribution(): AdAttribution | null {
  try {
    const raw = document.cookie
      .split("; ")
      .find((part) => part.startsWith(`${AD_ATTRIBUTION_COOKIE}=`))
      ?.slice(AD_ATTRIBUTION_COOKIE.length + 1)
    if (!raw) return null
    const parsed: unknown = JSON.parse(decodeURIComponent(raw))
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? sanitize(parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

export function writeAdAttribution(attribution: AdAttribution) {
  const value = encodeURIComponent(JSON.stringify(attribution))
  if (value.length > MAX_COOKIE_VALUE) return
  document.cookie = `${AD_ATTRIBUTION_COOKIE}=${value}; Max-Age=${MAX_AGE}${cookieAttributes()}`
}

export function clearAdAttribution() {
  document.cookie = `${AD_ATTRIBUTION_COOKIE}=; Max-Age=0${cookieAttributes()}`
}
